import React, { useEffect, useState } from 'react';
import Select, { MultiValue, SingleValue, StylesConfig } from 'react-select';
import { t } from 'i18next';
import TimeTaxSupportSystemClient from '../../system/TimeTaxSupportSystemClient';
import { Rubro } from '../../system/company/Rubro';

export const customStylesReactSelect: StylesConfig<OptionType> = {
  control: (provided) => ({
    ...provided,
    minHeight: 36,
    borderColor: 'rgb(96, 94, 92)',
    ':focus-within': { borderColor: 'rgb(0, 120, 212)', boxShadow: '0 0 0 1px rgb(0, 120, 212)' },
  }),
  menu: (provided) => ({ ...provided, zIndex: 1000 }),
  option: (base, { isSelected }) => ({
    ...base,
    ':hover': {
      background: 'rgb(243, 242, 241)',
      color: 'rgb(32, 31, 30)',
    },
    background: isSelected ? 'rgb(243, 242, 241)' : 'transparent',
    color: isSelected ? 'rgb(32, 31, 30)' : base.color,
  }),
};

interface RubroSelectorProps {
  system: TimeTaxSupportSystemClient;
  label?: string;
  onChangeSelected: (selectedRubros: Rubro[]) => void;
  defaultIds?: string[];
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  styles?: React.CSSProperties;
  maxSelection?: number;
}

interface OptionType {
  value: string;
  label: string;
}

const RubroSelector: React.FC<RubroSelectorProps> = ({
  system,
  label = t('Rubros'),
  onChangeSelected,
  defaultIds = [],
  disabled = false,
  placeholder = t('Seleccione hasta 2 rubros...'),
  className,
  styles,
  maxSelection = 2,
}) => {
  const [rubros, setRubros] = useState<Rubro[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<MultiValue<OptionType>>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchRubros = async () => {
      try {
        setLoading(true);

        const result = await system.getCompanySystem().getListRubros('empresa', 'list-rubros');
        setRubros(result);
      } catch (err) {
        console.error('Error fetching rubros:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchRubros();
  }, []);

  useEffect(() => {
    if (defaultIds && defaultIds.length > 0 && rubros.length > 0) {
      const opts = rubros.map((r) => ({ value: r.rub_id, label: r.rub_nombre }));
      const defaults = opts.filter((o) => defaultIds.includes(o.value));
      setSelectedOptions(defaults);
      onChangeSelected(rubros.filter((r) => defaultIds.includes(r.rub_id)));
    }
  }, [defaultIds, rubros]);

  const handleChange = (selected: MultiValue<OptionType> | SingleValue<OptionType>) => {
    // Aseguramos que selected sea un array válido
    const selectedArray: MultiValue<OptionType> = Array.isArray(selected) ? selected : [];
    const limited = selectedArray.slice(0, maxSelection);

    setSelectedOptions(limited);
    const selectedIds = limited.map((o) => o.value);
    onChangeSelected(rubros.filter((r) => selectedIds.includes(r.rub_id)));
  };

  const options: OptionType[] = rubros.map((r) => ({ value: r.rub_id, label: r.rub_nombre }));

  return (
    <div className={className} style={styles}>
      <label style={{ padding: '5px 0' }}>{label}</label>
      <Select
        isMulti
        isDisabled={disabled}
        isLoading={loading}
        noOptionsMessage={() => t('No se encontraron resultados')}
        loadingMessage={() => t('Cargando...')}
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        placeholder={loading ? t('Cargando...') : placeholder}
        styles={customStylesReactSelect}
        closeMenuOnSelect={false}
      />
    </div>
  );
};

export default RubroSelector;
